/*
 * <load-more> — Web Component for Shopify Prestige collection pages
 * ---------------------------------------------------------------------------
 * Fetches the next paginated page via Shopify's Section Rendering API
 *   docs: https://shopify.dev/docs/api/section-rendering
 *
 * Markup contract (rendered by sections/main-collection.liquid):
 *   <load-more
 *     data-section-id="{{ section.id }}"
 *     data-current-page="{{ paginate.current_page }}"
 *     data-total-pages="{{ paginate.pages }}"
 *     data-mode="load_more | infinite_scroll"
 *     data-target-id="product-list-{{ section.id }}"
 *   >
 *     <a href="{{ paginate.next.url }}" class="button …">Load more</a>
 *   </load-more>
 *
 * Behavior:
 *   - load_more       — clicking the inner <a>/<button> fetches the next page
 *   - infinite_scroll — IntersectionObserver auto-fires when the element
 *                       enters the viewport (rootMargin 300px)
 *
 * URL construction follows the prompt: read window.location.search, strip
 * any existing `page` and `section_id`, then merge in our section ID and the
 * next page number. This preserves active filters and sort in every path.
 *
 * Filter/sort re-init: free via Web Component lifecycle. Prestige's
 * facet:update listener (assets/theme.js) calls shopifySection.replaceChildren(),
 * which removes the old <load-more> (disconnectedCallback) and inserts the new
 * one (connectedCallback). No global listeners required.
 */
(function () {
  if (window.customElements.get('load-more')) return; // double-define guard

  class LoadMore extends HTMLElement {
    constructor() {
      super();
      this._loading = false;
      this._observer = null;
      this._onClick = this._onClick.bind(this);
      this._innerControl = null;
    }

    get sectionId()    { return this.dataset.sectionId; }
    get targetId()     { return this.dataset.targetId; }
    get mode()         { return this.dataset.mode || 'load_more'; }
    get currentPage()  { return parseInt(this.dataset.currentPage, 10) || 1; }
    get totalPages()   { return parseInt(this.dataset.totalPages, 10) || 1; }

    connectedCallback() {
      this._innerControl = this.querySelector('a, button');
      if (this._innerControl) {
        this._innerControl.addEventListener('click', this._onClick);
      }

      if (this.mode === 'infinite_scroll' && 'IntersectionObserver' in window) {
        this._observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this._load();
              break;
            }
          }
        }, { rootMargin: '300px 0px' });
        this._observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this._observer) { this._observer.disconnect(); this._observer = null; }
      if (this._innerControl) {
        this._innerControl.removeEventListener('click', this._onClick);
        this._innerControl = null;
      }
    }

    _onClick(event) {
      event.preventDefault();
      this._load();
    }

    _setBusy(busy) {
      if (!this._innerControl) return;
      this._innerControl.setAttribute('aria-busy', busy ? 'true' : 'false');
      this._innerControl.style.opacity = busy ? '0.6' : '';
      this._innerControl.style.pointerEvents = busy ? 'none' : '';
    }

    _buildNextUrl(nextPage) {
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      params.delete('section_id');
      if (this.sectionId) params.set('section_id', this.sectionId);
      params.set('page', String(nextPage));
      return window.location.pathname + '?' + params.toString();
    }

    async _load() {
      if (this._loading) return;
      const nextPage = this.currentPage + 1;
      if (nextPage > this.totalPages) {
        this.remove();
        return;
      }

      this._loading = true;
      this._setBusy(true);

      try {
        const url = this._buildNextUrl(nextPage);
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Append new product cards into the live grid
        const liveTarget = document.getElementById(this.targetId);
        const incomingTarget = doc.getElementById(this.targetId);
        let appendedNodes = [];

        if (liveTarget && incomingTarget) {
          const frag = document.createDocumentFragment();
          appendedNodes = Array.from(incomingTarget.children);
          for (const node of appendedNodes) {
            // Prestige hides any element carrying [reveal-on-scroll="true"] via CSS
            // (opacity:0). ProductList's reveal() observer ran at first paint and
            // ignores nodes added later, so leftover attributes keep new cards
            // invisible. Strip the attribute so cards render at default opacity.
            if (node.nodeType === 1) {
              if (node.hasAttribute && node.hasAttribute('reveal-on-scroll')) {
                node.removeAttribute('reveal-on-scroll');
              }
              if (node.querySelectorAll) {
                node.querySelectorAll('[reveal-on-scroll]').forEach((descendant) => {
                  descendant.removeAttribute('reveal-on-scroll');
                });
              }
            }
            frag.appendChild(node);
          }
          liveTarget.appendChild(frag);
        }

        // Notify the page (lazy-load images, non-WC consumers, etc.)
        this.dispatchEvent(new CustomEvent('load-more:appended', {
          bubbles: true,
          detail: { newNodes: appendedNodes, page: nextPage }
        }));

        // Advance state
        this.dataset.currentPage = String(nextPage);

        // If we've reached the last page, remove the element entirely.
        if (nextPage >= this.totalPages) {
          this.remove();
        }
      } catch (err) {
        console.warn('[load-more] failed to load next page', err);
      } finally {
        this._loading = false;
        this._setBusy(false);
      }
    }
  }

  window.customElements.define('load-more', LoadMore);
})();
